import { useState } from "react";
import { Sliders, Layers, Maximize2, Home, Cpu, MoreVertical } from "lucide-react";
import { useStore, PRODUCTS } from "@/store/useStore";

export default function TopBar() {
  const product = useStore((s) => s.product);
  const setProduct = useStore((s) => s.setProduct);
  const setEntered = useStore((s) => s.setEntered);
  const setMobilePanel = useStore((s) => s.setMobilePanel);
  const [overflow, setOverflow] = useState(false);

  return (
    <header
      data-testid="showroom-topbar"
      className="glass fixed inset-x-0 top-0 z-50 flex items-center justify-between gap-2 border-x-0 border-t-0 px-2 py-2.5 sm:px-6 sm:py-3"
    >
      {/* brand */}
      <button
        onClick={() => setEntered(false)}
        data-testid="reenter-landing-button"
        className="flex shrink-0 items-center gap-2.5"
        title="Return to landing"
      >
        <img src="/tab-seal.png" alt="TAB" className="h-9 w-9 rounded-full ring-1 ring-[#d4af37]/40" />
        <div className="hidden text-left md:block">
          <div className="font-display text-sm font-bold leading-none tracking-tight text-white">
            THE ACADEMIC BOARDROOM
          </div>
          <div className="eyebrow mt-0.5 text-[#d4af37]">TIME Inventions 2025</div>
        </div>
      </button>

      {/* product toggle */}
      <nav className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1">
        {Object.values(PRODUCTS).map((p) => (
          <button
            key={p.id}
            onClick={() => setProduct(p.id)}
            data-testid={`top-product-toggle-${p.id === "lotus" ? "lotus" : p.id}`}
            className={`pill rounded-full px-2.5 py-1.5 text-[0.7rem] font-medium sm:px-4 sm:text-xs ${
              product === p.id
                ? p.id === "melasyl"
                  ? "pill-active-violet"
                  : "pill-active-cyan"
                : "text-slate-300 hover:text-white"
            }`}
          >
            {p.id === "airpods" ? "AirPods" : p.id === "melasyl" ? "Melasyl" : "Lotus"}
          </button>
        ))}
      </nav>

      {/* status + actions */}
      <div className="flex shrink-0 items-center gap-2">
        <div className="mono hidden items-center gap-1.5 rounded-full border border-cyan-400/20 bg-cyan-400/5 px-2.5 py-1 text-[0.6rem] cyan-text xl:flex">
          <Cpu className="h-3 w-3" /> WebXR · 60 FPS
        </div>

        {/* sm→lg: labeled panel toggles */}
        <button
          onClick={() => setMobilePanel("product")}
          data-testid="mobile-open-product"
          className="pill hidden h-9 items-center gap-1.5 rounded-full border border-violet-400/30 bg-violet-500/10 px-3 text-xs font-medium text-violet-200 sm:flex lg:hidden"
        >
          <Layers className="h-4 w-4" /> Menu
        </button>
        <button
          onClick={() => setMobilePanel("controls")}
          data-testid="mobile-open-controls"
          className="pill hidden h-9 items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 text-xs font-medium cyan-text sm:flex lg:hidden"
        >
          <Sliders className="h-4 w-4" /> Controls
        </button>

        {/* phone (<sm): single overflow menu */}
        <div className="relative sm:hidden">
          <button
            onClick={() => setOverflow((o) => !o)}
            data-testid="mobile-open-overflow"
            className="pill flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/[0.05] text-slate-200"
            aria-label="Open panels menu"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {overflow && (
            <>
              <div className="fixed inset-0 z-[60]" onClick={() => setOverflow(false)} />
              <div
                data-testid="mobile-overflow-menu"
                className="glass absolute right-0 top-11 z-[61] w-44 overflow-hidden rounded-xl p-1.5"
              >
                <button
                  onClick={() => { setMobilePanel("product"); setOverflow(false); }}
                  data-testid="overflow-open-product"
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-xs text-violet-200 hover:bg-white/[0.06]"
                >
                  <Layers className="h-4 w-4" /> Interaction Menu
                </button>
                <button
                  onClick={() => { setMobilePanel("controls"); setOverflow(false); }}
                  data-testid="overflow-open-controls"
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-xs cyan-text hover:bg-white/[0.06]"
                >
                  <Sliders className="h-4 w-4" /> Spatial Controls
                </button>
                <button
                  onClick={() => { setEntered(false); setOverflow(false); }}
                  data-testid="overflow-home"
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-xs text-[#e6c65a] hover:bg-white/[0.06]"
                >
                  <Home className="h-4 w-4" /> Back to Landing
                </button>
              </div>
            </>
          )}
        </div>

        <button
          onClick={() => document.documentElement.requestFullscreen?.()}
          data-testid="fullscreen-button"
          className="pill hidden h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-slate-300 sm:flex"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
        <button
          onClick={() => setEntered(false)}
          data-testid="home-button"
          className="pill hidden h-9 w-9 items-center justify-center rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 text-[#e6c65a] sm:flex"
        >
          <Home className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}
